import React, { useState, useEffect } from 'react';
import { get, post, del } from 'aws-amplify/api';
import { useAuth } from '../../contexts/AuthContext';
import './SubscriberManagement.css';

const VALID_POSITIONS = [
  'Floor Manager', 'Host / Hostess', 'Server', 'Bartender',
  'Food Runner', 'Valet', 'Line Cook', 'Prep Cook', 'Dishwasher',
];

const FILTER_TYPE_COLORS = {
  ALL: { bg: '#f0fdf4', color: '#15803d' },
  FOH: { bg: '#dbeafe', color: '#1d4ed8' },
  BOH: { bg: '#dcfce7', color: '#15803d' },
  POSITION: { bg: '#fef3c7', color: '#92400e' },
};

const SubscriberManagement = () => {
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [newSubEmail, setNewSubEmail] = useState('');
  const [newSubFilterType, setNewSubFilterType] = useState('ALL');
  const [newSubFilterValues, setNewSubFilterValues] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState(null);
  const [subSuccess, setSubSuccess] = useState(null);
  const { getAuthToken } = useAuth();

  // --- Subscription Management ---
  const fetchSubscribers = async () => {
    try {
      setSubLoading(true);
      setSubError(null);

      const token = await getAuthToken();
      if (!token) {
        setSubError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasJobsAPI',
        path: '/v1/admin/jobs/subscriptions',
        options: {
          headers: { Authorization: token },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setSubscribers(data.subscriptions || []);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setSubError('Failed to fetch subscribers');
    } finally {
      setSubLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const restOperation = get({
        apiName: 'ChulasJobsAPI',
        path: '/v1/admin/jobs/admin-users',
        options: {
          headers: { Authorization: token },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setAdminUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching admin users:', err);
    }
  };

  const addSubscriber = async () => {
    try {
      setSubLoading(true);
      setSubError(null);
      setSubSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setSubError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasJobsAPI',
        path: '/v1/admin/jobs/subscriptions',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: {
            admin_email: newSubEmail,
            filter_type: newSubFilterType,
            filter_values: newSubFilterType === 'POSITION' ? newSubFilterValues : undefined,
          },
        },
      });

      await restOperation.response;

      setSubSuccess('Subscriber added successfully.');
      setNewSubEmail('');
      setNewSubFilterType('ALL');
      setNewSubFilterValues([]);
      await fetchSubscribers();
    } catch (err) {
      console.error('Error adding subscriber:', err);
      setSubError(err.message || 'Failed to add subscriber');
    } finally {
      setSubLoading(false);
    }
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from notification subscribers?`)) return;

    try {
      setSubLoading(true);
      setSubError(null);
      setSubSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setSubError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = del({
        apiName: 'ChulasJobsAPI',
        path: '/v1/admin/jobs/subscriptions',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: { admin_email: email },
        },
      });

      await restOperation.response;

      setSubSuccess('Subscriber removed.');
      await fetchSubscribers();
    } catch (err) {
      console.error('Error removing subscriber:', err);
      setSubError(err.message || 'Failed to remove subscriber');
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    if (showNotificationSettings) {
      fetchSubscribers();
      fetchAdminUsers();
    }
  }, [showNotificationSettings]);

  useEffect(() => {
    if (subSuccess) {
      const timer = setTimeout(() => setSubSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [subSuccess]);

  useEffect(() => {
    if (subError) {
      const timer = setTimeout(() => setSubError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [subError]);

  return (
    <div className="notification-settings-section">
      <button
        type="button"
        className="settings-toggle-btn"
        onClick={() => setShowNotificationSettings(!showNotificationSettings)}
      >
        <span>Email Notification Settings</span>
        <span className="toggle-icon">{showNotificationSettings ? '\u25B2' : '\u25BC'}</span>
      </button>

      {showNotificationSettings && (
        <div className="settings-panel">
          {subError && <div className="alert alert-error">{subError}</div>}
          {subSuccess && <div className="alert alert-success">{subSuccess}</div>}

          <div className="settings-subsection">
            <h3>Current Subscribers</h3>
            {subscribers.length === 0 ? (
              <p className="empty-message">No subscribers yet. Add one below.</p>
            ) : (
              <>
                <table className="subscribers-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Filter</th>
                      <th>Added By</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => (
                      <tr key={sub.admin_email}>
                        <td>{sub.admin_email}</td>
                        <td>
                          {sub.filter_type === 'POSITION' ? (
                            (sub.filter_values || [sub.filter_value]).filter(Boolean).map((v, i) => (
                              <span
                                key={i}
                                className="filter-badge"
                                style={{
                                  background: FILTER_TYPE_COLORS.POSITION.bg,
                                  color: FILTER_TYPE_COLORS.POSITION.color,
                                  marginRight: '4px',
                                }}
                              >
                                {v}
                              </span>
                            ))
                          ) : (
                            <span
                              className="filter-badge"
                              style={{
                                background: FILTER_TYPE_COLORS[sub.filter_type]?.bg,
                                color: FILTER_TYPE_COLORS[sub.filter_type]?.color,
                              }}
                            >
                              {sub.filter_type}
                            </span>
                          )}
                        </td>
                        <td>{sub.created_by || '\u2014'}</td>
                        <td>
                          <button
                            type="button"
                            className="action-btn deny-btn"
                            onClick={() => removeSubscriber(sub.admin_email)}
                            disabled={subLoading}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="subscriber-list-mobile">
                  {subscribers.map((sub) => (
                    <div className="subscriber-item" key={sub.admin_email}>
                      <div className="subscriber-item-header">
                        <span className="subscriber-email">{sub.admin_email}</span>
                        <button
                          type="button"
                          className="subscriber-remove"
                          onClick={() => removeSubscriber(sub.admin_email)}
                          disabled={subLoading}
                          aria-label={`Remove ${sub.admin_email}`}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="subscriber-item-filters">
                        {sub.filter_type === 'POSITION' ? (
                          (sub.filter_values || [sub.filter_value]).filter(Boolean).map((v, i) => (
                            <span
                              key={i}
                              className="filter-badge"
                              style={{
                                background: FILTER_TYPE_COLORS.POSITION.bg,
                                color: FILTER_TYPE_COLORS.POSITION.color,
                              }}
                            >
                              {v}
                            </span>
                          ))
                        ) : (
                          <span
                            className="filter-badge"
                            style={{
                              background: FILTER_TYPE_COLORS[sub.filter_type]?.bg,
                              color: FILTER_TYPE_COLORS[sub.filter_type]?.color,
                            }}
                          >
                            {sub.filter_type}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="settings-subsection">
            <h3>Add Subscriber</h3>
            <div className="add-subscriber-form">
              <div className="form-row">
                <label htmlFor="sub-email-select">Admin User</label>
                <select
                  id="sub-email-select"
                  value={newSubEmail}
                  onChange={(e) => setNewSubEmail(e.target.value)}
                >
                  <option value="">Select admin user...</option>
                  {adminUsers.map((u) => (
                    <option key={u.sub || u.email} value={u.email}>
                      {u.name !== u.email ? `${u.name} (${u.email})` : u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <label htmlFor="sub-filter-select">Filter</label>
                <select
                  id="sub-filter-select"
                  value={newSubFilterType}
                  onChange={(e) => { setNewSubFilterType(e.target.value); setNewSubFilterValues([]); }}
                >
                  <option value="ALL">All Applications</option>
                  <option value="FOH">FOH Only</option>
                  <option value="BOH">BOH Only</option>
                  <option value="POSITION">Specific Position</option>
                </select>
              </div>

              {newSubFilterType === 'POSITION' && (
                <div className="form-row">
                  <label>Positions</label>
                  <div className="position-checkboxes">
                    {VALID_POSITIONS.map((pos) => (
                      <label key={pos} className="position-checkbox-label">
                        <input
                          type="checkbox"
                          checked={newSubFilterValues.includes(pos)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewSubFilterValues([...newSubFilterValues, pos]);
                            } else {
                              setNewSubFilterValues(newSubFilterValues.filter(v => v !== pos));
                            }
                          }}
                        />
                        <span>{pos}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="button"
                className="btn btn-primary"
                onClick={addSubscriber}
                disabled={subLoading || !newSubEmail || (newSubFilterType === 'POSITION' && newSubFilterValues.length === 0)}
              >
                {subLoading ? 'Adding...' : 'Add Subscriber'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberManagement;
